import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Plus, Search, X, Layers, List, ChevronRight, ChevronDown, AlertTriangle, Bell, BellRing, Filter } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";
import { useAuth } from "../context/AuthContext";

const CARE_TYPES = ["shoeing", "deworming", "vaccination", "dental"];
const CARE_TYPE_LABELS = {
  shoeing: "Shoeing", deworming: "Deworming", vaccination: "Vaccination", dental: "Dental",
};

const RED = "#C0392B";
const AMBER = "#E8A838";

const RESTRICTION_TAG_LABEL = {
  box_rest: "Box rest",
  walker_only: "Walker only",
  no_jumping: "No jumping",
  no_paddock: "No paddock",
  light_work_only: "Light work only",
  other: "Restriction",
};
const fmtShortDate = (iso) => { try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return iso; } };

const statusToBadge = (s) => {
  switch (s) {
    case "active":   return "se-badge se-badge-active";
    case "injured":  return "se-badge se-badge-urgent";
    case "retired":  return "se-badge se-badge-inactive";
    case "sold":     return "se-badge se-badge-inactive";
    default:         return "se-badge se-badge-inactive";
  }
};

const empty = {
  name: "", nickname: "", stall_number: "", discipline: "", gender: "",
  height_cm: "", weight_kg: "",
  status: "active", owner_id: "", photo_url: null,
};

const UNASSIGNED_KEY = "__unassigned__";
const NO_STABLE_KEY = "__no_stable__";
const HORSES_VIEW_STORAGE_KEY = "stablos:horses:view"; // "grouped" | "flat"
const HORSES_COLLAPSED_STORAGE_KEY = "stablos:horses:collapsed"; // JSON array of collapsed group keys

// Read persisted view choice. Owner is always forced to "flat" (they only have
// one owner-group anyway, so a header would be pointless).
const readInitialView = (role) => {
  if (role === "owner") return "flat";
  try {
    const saved = localStorage.getItem(HORSES_VIEW_STORAGE_KEY);
    return saved === "flat" ? "flat" : "grouped";
  } catch { return "grouped"; }
};

export default function Horses() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const role = user?.role;
  const canCreateHorse = ["super_admin", "admin", "manager", "vet"].includes(role);
  const showCareColumn = !["driver", "owner", "handyman", "cleaner", "gardener"].includes(role);
  const showCareFilter = showCareColumn;

  // Care filter state. Legacy ?alert=care → opens with overdue selected.
  const initialStatus = params.get("alert") === "care" ? "overdue" : "all";
  const [careStatusFilter, setCareStatusFilter] = useState(initialStatus); // all | overdue | due_soon | all_clear
  const [careTypeFilter, setCareTypeFilter] = useState("all");
  const [careAlerts, setCareAlerts] = useState(null); // flat list from /api/dashboard/care-alerts

  const [rows, setRows] = useState(null);
  const [owners, setOwners] = useState([]);
  const [stableNames, setStableNames] = useState({}); // super_admin only: stable_id → name
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("all"); // "all" | owner_id | "unassigned"
  // ENH-014: default view is grouped-by-owner; owner-role forced to flat.
  const [view, setView] = useState(() => readInitialView(role));
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(HORSES_COLLAPSED_STORAGE_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(empty);
  const [addError, setAddError] = useState("");

  // Persist view choice (owner never persists — they're locked to "flat").
  useEffect(() => {
    if (role === "owner") return;
    try { localStorage.setItem(HORSES_VIEW_STORAGE_KEY, view); } catch { /* noop */ }
  }, [view, role]);

  // Persist collapsed-groups set.
  useEffect(() => {
    try { localStorage.setItem(HORSES_COLLAPSED_STORAGE_KEY, JSON.stringify(Array.from(collapsed))); } catch { /* noop */ }
  }, [collapsed]);

  const toggleCollapsed = (key) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const load = async () => {
    setError("");
    try {
      const [h, o] = await Promise.all([api.get("/horses"), api.get("/owners")]);
      setRows(h.data);
      setOwners(o.data);
    } catch (e) {
      setError(formatApiError(e));
      setRows([]);
    }
  };

  useEffect(() => { load(); }, []);

  // ENH-014: super_admin (un-impersonated) sees all stables — fetch names for
  // the outer group headers.
  useEffect(() => {
    if (role !== "super_admin") return;
    (async () => {
      try {
        const { data } = await api.get("/superadmin/stables");
        const m = {};
        (data || []).forEach((s) => { m[s.id] = s.name; });
        setStableNames(m);
      } catch { setStableNames({}); }
    })();
  }, [role]);

  useEffect(() => {
    if (!showCareFilter) {
      setCareAlerts([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/dashboard/care-alerts");
        setCareAlerts(data);
      } catch {
        setCareAlerts([]);
      }
    })();
  }, [showCareFilter]);

  // Strip legacy ?alert=care param after we have used it for the initial pill state
  useEffect(() => {
    if (params.get("alert") === "care") {
      const next = new URLSearchParams(params);
      next.delete("alert");
      setParams(next, { replace: true });
    }
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setAdding(true);
    setAddError("");
    try {
      if (!form.owner_id) {
        setAddError("Please assign an owner before saving.");
        return;
      }
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      await api.post("/horses", payload);
      setShowAdd(false);
      setForm(empty);
      await load();
    } catch (err) {
      setAddError(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const ownerNameById = useMemo(() => {
    const m = {};
    owners.forEach((o) => { m[o.id] = o.full_name; });
    return m;
  }, [owners]);

  // Group care alerts by horse_id, with optional care-type filtering
  const horseAlertMap = useMemo(() => {
    const map = new Map();
    (careAlerts || []).forEach((a) => {
      if (careTypeFilter !== "all" && a.care_type !== careTypeFilter) return;
      const entry = map.get(a.horse_id) || { overdue: [], due_soon: [] };
      if (a.status === "overdue") entry.overdue.push(a.care_type);
      else if (a.status === "due_soon") entry.due_soon.push(a.care_type);
      map.set(a.horse_id, entry);
    });
    return map;
  }, [careAlerts, careTypeFilter]);

  const counts = useMemo(() => {
    const overdueSet = new Set();
    const dueSoonSet = new Set();
    horseAlertMap.forEach((v, hid) => {
      if (v.overdue.length) overdueSet.add(hid);
      if (v.due_soon.length) dueSoonSet.add(hid);
    });
    const totalHorses = (rows || []).length;
    return {
      overdue: overdueSet.size,
      due_soon: dueSoonSet.size,
      all_clear: Math.max(0, totalHorses - new Set([...overdueSet, ...dueSoonSet]).size),
      all: totalHorses,
    };
  }, [horseAlertMap, rows]);

  const filtered = useMemo(() => {
    const list = rows || [];
    const q = search.toLowerCase();
    return list.filter((r) => {
      const alert = horseAlertMap.get(r.id);
      if (careStatusFilter === "overdue" && !(alert && alert.overdue.length)) return false;
      if (careStatusFilter === "due_soon" && !(alert && alert.due_soon.length)) return false;
      if (careStatusFilter === "all_clear" && alert && (alert.overdue.length || alert.due_soon.length)) return false;
      if (ownerFilter !== "all") {
        if (ownerFilter === "unassigned" && r.owner_id) return false;
        if (ownerFilter !== "unassigned" && r.owner_id !== ownerFilter) return false;
      }
      if (!q) return true;
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.nickname || "").toLowerCase().includes(q) ||
        (r.stall_number || "").toLowerCase().includes(q) ||
        (r.owner_name || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, ownerFilter, careStatusFilter, horseAlertMap]);

  const careCellFor = (horseId) => {
    const a = horseAlertMap.get(horseId);
    if (!a || (!a.overdue.length && !a.due_soon.length)) {
      return <span style={{ color: "#8C9E8A" }} data-testid={`care-cell-${horseId}`}>—</span>;
    }
    if (a.overdue.length) {
      return (
        <span className="inline-flex items-center gap-1" style={{ color: RED, fontWeight: 700 }} data-testid={`care-cell-${horseId}`}>
          <BellRing size={16} /> {a.overdue.length}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1" style={{ color: AMBER, fontWeight: 700 }} data-testid={`care-cell-${horseId}`}>
        <Bell size={16} /> {a.due_soon.length}
      </span>
    );
  };

  const rowBorder = (horseId) => {
    if (careStatusFilter === "all") return undefined;
    const a = horseAlertMap.get(horseId);
    if (!a) return undefined;
    if (a.overdue.length) return `3px solid ${RED}`;
    if (a.due_soon.length) return `3px solid ${AMBER}`;
    return undefined;
  };

  // ENH-014: build groups.
  //   • Flat view → null (fall through to the plain table).
  //   • Grouped view for non-super_admin → array of owner groups, horses inside
  //     sorted alphabetically by name, groups sorted alphabetically by owner
  //     with "Unassigned" pinned last.
  //   • Grouped view for super_admin → array of stable-level shells, each
  //     containing an inner owner-groups array with the same sort rules.
  const groups = useMemo(() => {
    if (view !== "grouped") return null;

    const alphaByName = (a, b) => (a.name || "").localeCompare(b.name || "");
    const buildOwnerGroups = (horses) => {
      const map = new Map();
      horses.forEach((h) => {
        const key = h.owner_id || UNASSIGNED_KEY;
        const label = h.owner_id ? (ownerNameById[h.owner_id] || h.owner_name || "Unknown owner") : "Unassigned";
        if (!map.has(key)) map.set(key, { key, label, horses: [] });
        map.get(key).horses.push(h);
      });
      const out = Array.from(map.values());
      out.forEach((g) => g.horses.sort(alphaByName));
      out.sort((a, b) => {
        if (a.key === UNASSIGNED_KEY) return 1;
        if (b.key === UNASSIGNED_KEY) return -1;
        return a.label.localeCompare(b.label);
      });
      return out;
    };

    if (role !== "super_admin") {
      return { kind: "owner", owners: buildOwnerGroups(filtered) };
    }

    // Super-admin: nest owner-groups inside stable shells.
    const byStable = new Map();
    filtered.forEach((h) => {
      const sKey = h.stable_id || NO_STABLE_KEY;
      const sLabel = h.stable_id ? (stableNames[h.stable_id] || "Unknown stable") : "No stable";
      if (!byStable.has(sKey)) byStable.set(sKey, { key: sKey, label: sLabel, horses: [] });
      byStable.get(sKey).horses.push(h);
    });
    const stableShells = Array.from(byStable.values());
    stableShells.forEach((s) => { s.owners = buildOwnerGroups(s.horses); });
    stableShells.sort((a, b) => {
      if (a.key === NO_STABLE_KEY) return 1;
      if (b.key === NO_STABLE_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
    return { kind: "stable", stables: stableShells };
  }, [filtered, view, ownerNameById, role, stableNames]);

  const renderRow = (h) => (
    <tr
      key={h.id}
      onClick={() => navigate(`/horses/${h.id}`)}
      style={{ cursor: "pointer", borderLeft: rowBorder(h.id) }}
      data-testid={`horse-row-${h.id}`}
    >
      <td style={{ fontWeight: 600 }}>
        {h.name}
        {(h.active_restrictions || []).length > 0 && (
          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21", fontWeight: 500 }} data-testid={`horse-restriction-tag-${h.id}`}>
            <span style={{ fontSize: 10 }}>⚠</span>
            {RESTRICTION_TAG_LABEL[h.active_restrictions[0].restriction_type] || "Restriction"}
            {" until "}{fmtShortDate(h.active_restrictions[0].end_date)}
          </div>
        )}
      </td>
      <td>{h.nickname || "—"}</td>
      <td>{h.stall_number || "—"}</td>
      <td>{h.owner_name || "—"}</td>
      <td><span className={statusToBadge(h.status)}>{h.status}</span></td>
      {showCareColumn && <td>{careCellFor(h.id)}</td>}
    </tr>
  );

  // Sorted flat list (spec: flat view = alphabetical by horse name).
  const sortedFiltered = useMemo(
    () => [...filtered].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [filtered],
  );

  const renderOwnerGroup = (g) => {
    const isCollapsed = collapsed.has(g.key);
    const horseCountLabel = g.horses.length === 1 ? "1 horse" : `${g.horses.length} horses`;
    const Chevron = isCollapsed ? ChevronRight : ChevronDown;
    return (
      <div key={g.key} className="se-card p-0 overflow-hidden" data-testid={`horse-group-${g.key}`}>
        <div
          className="px-5 py-3 flex items-center justify-between cursor-pointer select-none"
          style={{ background: "rgba(212,169,106,0.12)", borderBottom: isCollapsed ? "none" : "0.5px solid rgba(212,169,106,0.3)" }}
          onClick={() => toggleCollapsed(g.key)}
          role="button"
          aria-expanded={!isCollapsed}
          data-testid={`horse-group-header-${g.key}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Chevron size={16} style={{ color: "#7C5C3A", flexShrink: 0 }} />
            <span className="font-heading truncate" style={{ fontSize: 15, color: "#7C5C3A", fontWeight: 700 }}>{g.label}</span>
            <span className="se-badge se-badge-inactive" data-testid={`horse-group-count-${g.key}`}>{horseCountLabel}</span>
          </div>
          {g.key !== UNASSIGNED_KEY && (
            <button
              className="se-link inline-flex items-center gap-1 text-sm"
              onClick={(e) => { e.stopPropagation(); navigate(`/owners/${g.key}`); }}
              data-testid={`view-owner-${g.key}`}
            >
              View owner <ChevronRight size={14} />
            </button>
          )}
        </div>
        {!isCollapsed && (
          <div className="overflow-x-auto">
            <table className="se-table w-full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Nickname</th>
                  <th>Stall</th>
                  <th>Owner</th>
                  <th>Status</th>
                  {showCareColumn && <th>Care</th>}
                </tr>
              </thead>
              <tbody>{g.horses.map(renderRow)}</tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="horses-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="se-label">Roster</div>
          <h1 className="font-heading mt-1" style={{ fontSize: 28 }}>Horses</h1>
          <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
            All horses in your stable — scoped to your tenant only.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8C9E8A" }} />
            <input
              className="se-input"
              style={{ paddingLeft: 36, minWidth: 220 }}
              placeholder="Search name, breed, stall…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="horses-search-input"
            />
          </div>
          {/* ENH-014: view toggle — hidden for owner role (they only see their own horses). */}
          {role !== "owner" && (
            <div
              className="inline-flex rounded-lg overflow-hidden"
              style={{ border: "0.5px solid rgba(124,92,58,0.35)" }}
              data-testid="horses-view-toggle"
            >
              <button
                type="button"
                onClick={() => setView("grouped")}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm"
                style={{
                  background: view === "grouped" ? "#7C5C3A" : "transparent",
                  color: view === "grouped" ? "#F5EFE6" : "#7C5C3A",
                  fontWeight: 600,
                }}
                data-testid="horses-view-grouped"
                aria-pressed={view === "grouped"}
                title="Grouped by owner"
              >
                <Layers size={14} />
                <span className="hidden sm:inline">Grouped</span>
              </button>
              <button
                type="button"
                onClick={() => setView("flat")}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm"
                style={{
                  background: view === "flat" ? "#7C5C3A" : "transparent",
                  color: view === "flat" ? "#F5EFE6" : "#7C5C3A",
                  fontWeight: 600,
                }}
                data-testid="horses-view-flat"
                aria-pressed={view === "flat"}
                title="Flat list"
              >
                <List size={14} />
                <span className="hidden sm:inline">Flat</span>
              </button>
            </div>
          )}
          {canCreateHorse && (
            <button className="se-btn" onClick={() => setShowAdd(true)} data-testid="horses-add-button">
              <Plus size={16} /> Add horse
            </button>
          )}
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="se-label" style={{ minWidth: 60 }}>Owner</span>
          <select
            className="se-input"
            style={{ minWidth: 200 }}
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            data-testid="horses-owner-filter"
          >
            <option value="all">All owners</option>
            <option value="unassigned">Unassigned</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>{o.full_name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 text-sm px-3 py-2 rounded-lg"
          style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
          data-testid="horses-error"
        >
          {error}
        </div>
      )}

      {showCareFilter && (
        <CareFilterBar
          status={careStatusFilter}
          setStatus={setCareStatusFilter}
          careType={careTypeFilter}
          setCareType={setCareTypeFilter}
          counts={counts}
        />
      )}

      {view === "flat" && (
        <div className="se-card p-0 overflow-hidden" data-testid="horses-table-card">
          <div className="overflow-x-auto">
            <table className="se-table w-full" data-testid="horses-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Nickname</th>
                  <th>Stall</th>
                  <th>Owner</th>
                  <th>Status</th>
                  {showCareColumn && <th>Care</th>}
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={showCareColumn ? 6 : 5} style={{ color: "#8C9E8A" }}>Loading…</td></tr>
                )}
                {rows !== null && sortedFiltered.length === 0 && (
                  <tr>
                    <td colSpan={showCareColumn ? 6 : 5} style={{ color: "#8C9E8A" }} data-testid="horses-empty">
                      No horses match your filter. Try clearing search or filters.
                    </td>
                  </tr>
                )}
                {sortedFiltered.map(renderRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "grouped" && groups && groups.kind === "owner" && (
        <div className="space-y-5" data-testid="horses-grouped">
          {groups.owners.length === 0 && (
            <div className="se-card" style={{ color: "#8C9E8A" }} data-testid="horses-empty">
              No horses match your filter.
            </div>
          )}
          {groups.owners.map(renderOwnerGroup)}
        </div>
      )}

      {view === "grouped" && groups && groups.kind === "stable" && (
        <div className="space-y-6" data-testid="horses-grouped-super">
          {groups.stables.length === 0 && (
            <div className="se-card" style={{ color: "#8C9E8A" }} data-testid="horses-empty">
              No horses match your filter.
            </div>
          )}
          {groups.stables.map((s) => {
            const stableCollapsed = collapsed.has(`stable:${s.key}`);
            const StableChevron = stableCollapsed ? ChevronRight : ChevronDown;
            return (
              <div key={s.key} className="space-y-3" data-testid={`horse-stable-${s.key}`}>
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer select-none"
                  style={{ background: "rgba(124,92,58,0.08)", border: "0.5px solid rgba(124,92,58,0.25)" }}
                  onClick={() => toggleCollapsed(`stable:${s.key}`)}
                  role="button"
                  aria-expanded={!stableCollapsed}
                  data-testid={`horse-stable-header-${s.key}`}
                >
                  <StableChevron size={16} style={{ color: "#3B2F1E" }} />
                  <span className="font-heading" style={{ fontSize: 16, color: "#3B2F1E", fontWeight: 700 }}>{s.label}</span>
                  <span className="se-badge se-badge-inactive">
                    {s.horses.length === 1 ? "1 horse" : `${s.horses.length} horses`}
                  </span>
                </div>
                {!stableCollapsed && (
                  <div className="space-y-4 pl-3 md:pl-6">
                    {s.owners.map(renderOwnerGroup)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center p-4"
          style={{ background: "rgba(59,47,30,0.5)" }}
          onClick={() => setShowAdd(false)}
          data-testid="horse-modal-backdrop"
        >
          <form
            onSubmit={create}
            onClick={(e) => e.stopPropagation()}
            className="se-card w-full max-w-lg"
            data-testid="horse-add-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading" style={{ fontSize: 18 }}>Add a new horse</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="se-btn-ghost se-btn" data-testid="horse-modal-close">
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="se-label block mb-1">Name</label>
                <input className="se-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="horse-form-name" />
              </div>
              <div>
                <label className="se-label block mb-1">Nickname</label>
                <input className="se-input" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} data-testid="horse-form-nickname" />
              </div>
              <div>
                <label className="se-label block mb-1">Stall #</label>
                <input className="se-input" value={form.stall_number} onChange={(e) => setForm({ ...form, stall_number: e.target.value })} data-testid="horse-form-stall" />
              </div>
              <div>
                <label className="se-label block mb-1">Height (cm)</label>
                <input type="number" min="0" max="400" step="0.1" className="se-input" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} data-testid="horse-form-height" />
              </div>
              <div>
                <label className="se-label block mb-1">Weight (kg)</label>
                <input type="number" min="0" max="2000" step="0.1" className="se-input" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="horse-form-weight" />
              </div>
              <div>
                <label className="se-label block mb-1">Gender</label>
                <select className="se-input" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="horse-form-gender">
                  <option value="">—</option>
                  <option value="stallion">Stallion</option>
                  <option value="mare">Mare</option>
                  <option value="gelding">Gelding</option>
                </select>
              </div>
              <div>
                <label className="se-label block mb-1">Discipline</label>
                <select className="se-input" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} data-testid="horse-form-discipline">
                  <option value="">—</option>
                  <option value="show_jumping">Show Jumping</option>
                  <option value="dressage">Dressage</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="se-label block mb-1">Status</label>
                <select className="se-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="horse-form-status">
                  <option value="active">Active</option>
                  <option value="injured">Injured</option>
                  <option value="retired">Retired</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="se-label block mb-1">Owner <span style={{ color: "#C0392B" }}>*</span></label>
                <select
                  className="se-input"
                  value={form.owner_id}
                  onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                  data-testid="horse-form-owner"
                  aria-invalid={!form.owner_id}
                >
                  <option value="">— Select an owner —</option>
                  {owners.map((o) => (
                    <option key={o.id} value={o.id}>{o.full_name}</option>
                  ))}
                </select>
                {owners.length === 0 && (
                  <p className="text-xs mt-1" style={{ color: "#C0392B" }} data-testid="horse-form-owner-hint">
                    You must add an owner first before creating a horse.
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <label className="se-label block mb-1">Profile photo</label>
                <PhotoUpload
                  value={form.photo_url}
                  onChange={(url) => setForm({ ...form, photo_url: url })}
                  purpose="horse"
                  aspect="landscape"
                  hint="Landscape · 600×400 recommended · max 2MB"
                  testid="horse-add-photo"
                />
              </div>
            </div>

            {addError && (
              <div
                className="mt-4 text-sm px-3 py-2 rounded-lg"
                style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
                data-testid="horse-form-error"
              >
                {addError}
              </div>
            )}

            <div className="flex gap-3 mt-5 justify-end">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setShowAdd(false)} data-testid="horse-form-cancel">
                Cancel
              </button>
              <button type="submit" className="se-btn" disabled={adding} data-testid="horse-form-submit">
                {adding ? "Adding…" : "Add horse"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function CareFilterBar({ status, setStatus, careType, setCareType, counts }) {
  const STATUS_OPTIONS = [
    { key: "all",       label: "All horses",  color: "#7C5C3A", count: counts.all },
    { key: "overdue",   label: "Overdue",     color: RED,       count: counts.overdue },
    { key: "due_soon",  label: "Due soon",    color: AMBER,     count: counts.due_soon },
    { key: "all_clear", label: "All clear",   color: "#4A7C59", count: counts.all_clear },
  ];

  const active = STATUS_OPTIONS.find((o) => o.key === status) || STATUS_OPTIONS[0];

  return (
    <div className="mb-4" data-testid="care-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2" data-testid="care-filter-status-group">
          {STATUS_OPTIONS.map((opt) => {
            const isActive = opt.key === status;
            const showCountBadge = (opt.key === "overdue" || opt.key === "due_soon") && opt.count > 0;
            return (
              <button
                key={opt.key}
                onClick={() => setStatus(opt.key)}
                className="inline-flex items-center gap-2"
                style={{
                  padding: "8px 14px",
                  borderRadius: 20,
                  border: `1px solid ${isActive ? opt.color : "rgba(212,169,106,0.4)"}`,
                  background: isActive ? `${opt.color}33` : "#FDFAF6",
                  color: isActive ? opt.color : "#3B2F1E",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 150ms ease, border-color 150ms ease",
                }}
                data-testid={`care-filter-status-${opt.key}`}
                aria-pressed={isActive}
              >
                {opt.label}
                {showCountBadge && (
                  <span
                    style={{
                      background: opt.color,
                      color: "#FDFAF6",
                      padding: "1px 8px",
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                    data-testid={`care-filter-count-${opt.key}`}
                  >
                    {opt.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 md:ml-2 w-full md:w-auto">
          <span className="se-label" style={{ minWidth: 70 }}>Care type</span>
          <select
            className="se-input"
            style={{ minWidth: 160 }}
            value={careType}
            onChange={(e) => setCareType(e.target.value)}
            data-testid="care-filter-type-select"
          >
            <option value="all">All types</option>
            {CARE_TYPES.map((t) => (
              <option key={t} value={t}>{CARE_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
      </div>

      {(status !== "all" || careType !== "all") && (
        <div
          className="mt-2 flex items-center justify-between px-3 py-2 rounded-lg"
          style={{
            background: `${active.color}1F`,
            color: active.color,
            border: `0.5px solid ${active.color}55`,
            fontSize: 13,
          }}
          data-testid="care-filter-summary"
        >
          <span className="inline-flex items-center gap-2">
            <Filter size={14} />
            <span>
              <strong>Showing:</strong> {active.label}
              {careType !== "all" ? ` · ${CARE_TYPE_LABELS[careType]}` : ""}
              {" · "}
              {status === "overdue" || status === "due_soon"
                ? `${active.count} horse${active.count === 1 ? "" : "s"} need attention`
                : status === "all_clear"
                ? `${counts.all_clear} horse${counts.all_clear === 1 ? "" : "s"} on track`
                : ""}
            </span>
          </span>
          <button
            className="se-btn se-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 12 }}
            onClick={() => { setStatus("all"); setCareType("all"); }}
            data-testid="care-filter-clear"
          >
            <X size={12} /> Clear filter
          </button>
        </div>
      )}
    </div>
  );
}
